import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

/**
 * Presentation-only PowerPoint player for learners.
 *
 * Wraps the standalone Angular 21 viewer served from /pptx-viewer/ (source: cake4/pptx-viewer)
 * in an iframe. The file URL must be on the same origin; it is fetched with the user's cookies.
 * Fullscreen is disabled by the iframe's permissions policy so learners stay on the page.
 *
 *   <pptx-player [src]="'/files/' + fileId" height="560px" (loaded)="onLoaded($event)"></pptx-player>
 */
@Component({
  selector: 'pptx-player',
  templateUrl: './pptx-player.component.html',
  styleUrls: ['./pptx-player.component.scss']
})
export class PptxPlayerComponent implements OnChanges {
  @Input() src: string;
  @Input() height: string = '560px';

  /** Total number of slides, once the deck has been parsed. */
  @Output() loaded = new EventEmitter<number>();
  /** Zero-based index of the slide the learner moved to. */
  @Output() slideChange = new EventEmitter<number>();
  @Output() error = new EventEmitter<string>();

  @ViewChild('frame', { static: false }) frame: ElementRef<HTMLIFrameElement>;

  frameSrc: SafeResourceUrl;

  constructor(private sanitizer: DomSanitizer) { }

  ngOnChanges() {
    this.frameSrc = this.src
      ? this.sanitizer.bypassSecurityTrustResourceUrl('/pptx-viewer/index.html?src=' + encodeURIComponent(this.src))
      : null;
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    if (event.origin !== window.location.origin || !this.frame || event.source !== this.frame.nativeElement.contentWindow) {
      return;
    }
    const data = event.data || {};
    switch (data.type) {
      case 'pptx-viewer:loaded':
        this.loaded.emit(data.slideCount);
        break;
      case 'pptx-viewer:slide':
        this.slideChange.emit(data.index);
        break;
      case 'pptx-viewer:error':
        this.error.emit(data.message);
        break;
    }
  }
}
